import createUnsignedRequest from './createUnsignedRequest';

const { apiURL } = require('./apiURL');

const Types = {
  PAYLOAD: 'PUBLIC_AGREEMENT_PAYLOAD',
  LOADING: 'PUBLIC_AGREEMENT_LOADING',
  ERROR: 'PUBLIC_AGREEMENT_ERROR',
  ACCEPT: 'PUBLIC_AGREEMENT_ACCEPT',
  CLEAR: 'PUBLIC_AGREEMENT_CLEAR',
};

async function parseJsonResponse(res) {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { parseError: true, text };
  }
}

/**
 * @param {string} token
 */
const getPublicAgreement = (token) => {
  return async (dispatch) => {
    dispatch({ type: Types.LOADING, value: true });
    dispatch({ type: Types.ERROR, value: null });
    try {
      const res = await createUnsignedRequest(
        'GET',
        apiURL + '/public/agreements/' + encodeURIComponent(token),
        null,
        {}
      );
      if (!res.ok) {
        dispatch({ type: Types.PAYLOAD, value: null });
        dispatch({ type: Types.ERROR, value: res.status === 404 ? 'not_found' : 'error' });
        return null;
      }
      const data = await parseJsonResponse(res);
      dispatch({ type: Types.PAYLOAD, value: data });
      return data;
    } catch (e) {
      console.log('getPublicAgreement Error', e);
      dispatch({ type: Types.ERROR, value: e.message });
      return null;
    } finally {
      dispatch({ type: Types.LOADING, value: false });
    }
  };
};

/**
 * @param {string} token
 * @param {{ documents: { documentKind: string, versionLabel: string, artifactSha256: string }[], signer: { name: string, email: string, title: string } }} body
 */
const acceptPublicAgreement = (token, body) => {
  return async (dispatch) => {
    dispatch({ type: Types.LOADING, value: true });
    try {
      const res = await createUnsignedRequest(
        'POST',
        apiURL + '/public/agreements/' + encodeURIComponent(token) + '/accept',
        JSON.stringify(body),
        {}
      );
      const data = res.ok ? await parseJsonResponse(res) : null;
      if (res.status === 409) {
        dispatch({ type: Types.ERROR, value: 'snapshot_validation_failed' });
        return { ok: false, code: 'snapshot_validation_failed', data };
      }
      if (!res.ok) {
        const errData = data;
        dispatch({ type: Types.ERROR, value: errData?.code || 'accept_failed' });
        return { ok: false, code: errData?.code, data: errData };
      }
      dispatch({ type: Types.ACCEPT, value: data });
      return { ok: true, data };
    } catch (e) {
      dispatch({ type: Types.ERROR, value: e.message });
      return { ok: false, error: e.message };
    } finally {
      dispatch({ type: Types.LOADING, value: false });
    }
  };
};

const createPublicCheckoutSession = (token) => {
  return async (dispatch) => {
    try {
      const res = await createUnsignedRequest(
        'POST',
        apiURL + '/public/agreements/' + encodeURIComponent(token) + '/checkout-session',
        null,
        {}
      );
      const data = res.ok ? await parseJsonResponse(res) : null;
      if (!res.ok) {
        return { ok: false, data };
      }
      return { ok: true, data };
    } catch (e) {
      return { ok: false, error: e.message };
    }
  };
};

/**
 * Poll the payment hand-off after a card pay-mode accept.
 * @param {string} contractId - from the accept response
 * @param {string} key - `paymentStatusKey` from the accept response
 * @returns {Promise<{ ok: boolean, data?: { status: 'provisioning'|'checkout_ready'|'paid'|'awaiting_invoice', checkoutUrl: string|null } }>}
 */
const getPublicPaymentStatus = async (contractId, key) => {
  try {
    const res = await createUnsignedRequest(
      'GET',
      `${apiURL}/public/agreements/payment-status/${encodeURIComponent(contractId)}?key=${encodeURIComponent(key)}`,
      null,
      {}
    );
    if (!res.ok) return { ok: false };
    return { ok: true, data: await parseJsonResponse(res) };
  } catch (e) {
    return { ok: false, error: e.message };
  }
};

/**
 * Resolve CYF contracting entity without Cognito (registration, marketing, pre-org flows).
 * @param {{ billingCountryCode?: string, incorporationCountryCode?: string, whiteLabelId?: string }} params
 * @returns {Promise<{ entity?: object, jurisdictionScheduleVersion?: string, whiteLabelId?: string, error?: string, status?: number }>}
 */
const getPublicContractingEntity = async (params = {}) => {
  const query = new URLSearchParams();
  if (params.whiteLabelId != null && params.whiteLabelId !== '') {
    query.set('whiteLabelId', String(params.whiteLabelId));
  }
  if (params.billingCountryCode) query.set('billingCountryCode', params.billingCountryCode);
  if (params.incorporationCountryCode) {
    query.set('incorporationCountryCode', params.incorporationCountryCode);
  }

  const qs = query.toString();
  const url = apiURL + '/public/legal/contracting-entity' + (qs ? `?${qs}` : '');

  try {
    const res = await createUnsignedRequest('GET', url, null, {});
    const data = res.ok ? await parseJsonResponse(res) : null;
    if (!res.ok) {
      return {
        error: data?.error || (res.status === 404 ? 'not_found' : 'request_failed'),
        status: res.status,
      };
    }
    return data;
  } catch (e) {
    console.log('getPublicContractingEntity Error', e);
    return { error: 'request_failed', status: 0 };
  }
};

/**
 * Fetch public legal markdown (no org header).
 * @param {string} path - e.g. from legalDocuments[].markdownPath or `/public/legal/markdown/...`
 */
const getPublicLegalMarkdown = async (path) => {
  if (!path) return null;
  const url = path.startsWith('http') ? path : path.startsWith('/') ? apiURL.replace(/\/$/, '') + path : apiURL + path;
  const res = await fetch(url, { headers: { Accept: 'text/markdown' } });
  if (!res.ok) return null;
  return res.text();
};

const clearPublicAgreement = () => ({ type: Types.CLEAR });

const Actions = {
  getPublicAgreement,
  acceptPublicAgreement,
  createPublicCheckoutSession,
  getPublicPaymentStatus,
  getPublicContractingEntity,
  getPublicLegalMarkdown,
  clearPublicAgreement,
};

export { Actions, Types };
